import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export type LiftMoveSource = 'drag' | 'keyboard';

export interface UseLiftAndMoveLabels {
  /** Live-region announcement when a keyboard lift starts. */
  liftedAnnouncement: (index: number, total: number) => string;
  /** Live-region announcement after each keyboard move. */
  movedAnnouncement: (index: number, total: number) => string;
  /** Live-region announcement when a lift is committed (drop). */
  droppedAnnouncement: (index: number, total: number) => string;
  /** Live-region announcement when a lift is cancelled (Escape/blur). */
  cancelledAnnouncement: string;
}

export interface UseLiftAndMoveOptions<TPos> {
  /** Every position a lift may occupy, in the order Arrow keys step through. */
  positions: TPos[];
  /** Stable identity of whatever a position currently represents. */
  keyOf: (pos: TPos) => string;
  /** Whether a lift may begin at `pos` at all. */
  isLiftable: (pos: TPos) => boolean;
  /** Whether stepping (or dropping) from `from` to `to` is legal. */
  canMove: (from: TPos, to: TPos) => boolean;
  /**
   * `'live'` (default — `ReorderableListMolecule`): every Arrow step commits
   * immediately through `onMove`, so the list visually reorders as the user
   * moves; a cancel (Escape/blur) replays the net steps back through `onMove`
   * since the move already happened.
   *
   * `'preview'` (the filter tree's keyboard drag-to-group): Arrow steps only
   * move the cursor — `onMove` is never called mid-lift. Enter commits once,
   * through `onLiftEnd`, with the final position. A cancel simply drops the
   * cursor; there is nothing to replay because nothing committed.
   */
  commitMode?: 'live' | 'preview';
  /** Fired on every committed step in `'live'` mode. Unused in `'preview'` mode. */
  onMove?: (from: TPos, to: TPos, source: LiftMoveSource) => void;
  /** Fired once when a lift drops with net movement (`from !== to`). */
  onLiftEnd?: (from: TPos, to: TPos) => void;
  /** Fired whenever a lift interaction ends, however it ended — drop, no-op drop, or cancel. */
  onLiftSettled?: () => void;
  disabled?: boolean;
  labels: UseLiftAndMoveLabels;
}

export interface UseLiftAndMoveResult<TPos> {
  /** The position currently lifted (mid-drag/mid-lift), or `null` when nothing is lifted. */
  liftedPos: TPos | null;
  announcement: string;
  isLifted: (pos: TPos) => boolean;
  /** Registers (or, on unmount, unregisters with `el=null`) the keyboard handle for `key`. */
  registerHandle: (key: string, el: HTMLButtonElement | null) => void;
  /** Wire to the handle's own `onKeyDown`. `pos` is only consulted to seed a fresh lift. */
  handleKeyDown: (e: React.KeyboardEvent, pos: TPos) => void;
  /** Wire to the handle's own `onBlur`, passing the same key it was registered under. */
  handleBlur: (key: string) => void;
}

/**
 * Lift/move/drop/cancel/announce state machine shared by a keyboard reordering
 * interaction, extracted out of `ReorderableListMolecule` (its original, only
 * consumer — `commitMode: 'live'`) so `UnifiedFilterTreeMolecule`'s drag-to-group
 * keyboard lift (`commitMode: 'preview'`) can reuse the same lift/cancel/announce
 * mechanics instead of re-deriving them. The two interactions commit differently
 * (live splice-per-step vs. a single drop) — `commitMode` is the one axis they
 * actually vary on; everything else (staleness, blur-cancels-a-lift, announce
 * text, re-focusing the handle) is identical.
 */
export function useLiftAndMove<TPos>({
  positions,
  keyOf,
  isLiftable,
  canMove,
  commitMode = 'live',
  onMove,
  onLiftEnd,
  onLiftSettled,
  disabled = false,
  labels,
}: UseLiftAndMoveOptions<TPos>): UseLiftAndMoveResult<TPos> {
  const [liftedPos, setLiftedPos] = useState<TPos | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const liftStartRef = useRef<TPos | null>(null);
  const liftedKeyRef = useRef<string | null>(null);
  const handleRefs = useRef(new Map<string, HTMLButtonElement>());

  // `'live'` mode: a lift becomes stale once the position no longer holds the
  // item that was lifted (removed from `positions`, or something else now
  // occupies it — e.g. a consumer's own controls reordered the list mid-lift).
  // `'preview'` mode has no "occupant" concept — a lift is only stale once its
  // cursor position stops existing at all (its group was removed mid-lift).
  const liftIsStale =
    liftedPos !== null &&
    (!positions.includes(liftedPos) ||
      (commitMode === 'live' && keyOf(liftedPos) !== liftedKeyRef.current));
  const activeLift = liftedPos !== null && !liftIsStale ? liftedPos : null;

  const indexOf = useCallback((pos: TPos) => positions.indexOf(pos), [positions]);

  const settle = useCallback(() => {
    setLiftedPos(null);
    liftedKeyRef.current = null;
    liftStartRef.current = null;
    onLiftSettled?.();
  }, [onLiftSettled]);

  const cancelLift = useCallback(
    (pos: TPos) => {
      if (activeLift === null || keyOf(activeLift) !== keyOf(pos)) return;
      const start = liftStartRef.current;
      if (commitMode === 'live' && start !== null && !Object.is(start, activeLift)) {
        onMove?.(activeLift, start, 'keyboard');
      }
      setAnnouncement(labels.cancelledAnnouncement);
      settle();
    },
    [activeLift, commitMode, keyOf, onMove, labels, settle]
  );

  const handleBlur = useCallback(
    (key: string) => {
      // Checked a tick later: focus a move (or a re-render) restores to this same
      // handle right after was never actually lost.
      setTimeout(() => {
        if (handleRefs.current.get(key) === document.activeElement) return;
        if (activeLift !== null && keyOf(activeLift) === key) cancelLift(activeLift);
      }, 0);
    },
    [activeLift, keyOf, cancelLift]
  );

  useLayoutEffect(() => {
    if (activeLift === null) return;
    const handle = handleRefs.current.get(keyOf(activeLift));
    if (handle && document.activeElement !== handle) handle.focus();
  }, [activeLift, keyOf]);

  const registerHandle = useCallback((key: string, el: HTMLButtonElement | null) => {
    if (el) handleRefs.current.set(key, el);
    else handleRefs.current.delete(key);
  }, []);

  const beginLift = useCallback(
    (pos: TPos) => {
      setLiftedPos(pos);
      liftedKeyRef.current = keyOf(pos);
      liftStartRef.current = pos;
      setAnnouncement(labels.liftedAnnouncement(indexOf(pos), positions.length));
    },
    [keyOf, indexOf, positions, labels]
  );

  // Steps to the NEAREST legal position in the arrow's direction, not merely
  // the adjacent one: an illegal position (e.g. the lifted group itself, or
  // one of its descendants) is passed over rather than becoming a wall the
  // keyboard can never get past (ux-craft A1 — every target a pointer drag
  // can reach is reachable by keyboard). `canMove` is the same from→to
  // predicate a pointer drop consults, so skipping never reaches a position a
  // drop could not. No legal position in that direction leaves the lift where
  // it is.
  const stepLift = useCallback(
    (from: TPos, direction: 1 | -1) => {
      let toIdx = indexOf(from) + direction;
      while (toIdx >= 0 && toIdx < positions.length && !canMove(from, positions[toIdx])) {
        toIdx += direction;
      }
      if (toIdx < 0 || toIdx >= positions.length) return;
      const to = positions[toIdx];
      if (commitMode === 'live') onMove?.(from, to, 'keyboard');
      setLiftedPos(to);
      setAnnouncement(labels.movedAnnouncement(toIdx, positions.length));
    },
    [indexOf, positions, canMove, commitMode, onMove, labels]
  );

  const dropLift = useCallback(
    (pos: TPos) => {
      setAnnouncement(labels.droppedAnnouncement(indexOf(pos), positions.length));
      const start = liftStartRef.current;
      if (start !== null && !Object.is(start, pos)) {
        onLiftEnd?.(start, pos);
      }
      settle();
    },
    [indexOf, positions, onLiftEnd, settle, labels]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, pos: TPos) => {
      if (disabled) return;

      if (activeLift === null) {
        if ((e.key === ' ' || e.key === 'Enter') && isLiftable(pos)) {
          e.preventDefault();
          beginLift(pos);
        }
        return;
      }

      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        stepLift(activeLift, e.key === 'ArrowUp' ? -1 : 1);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        dropLift(activeLift);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelLift(activeLift);
      }
    },
    [disabled, activeLift, isLiftable, beginLift, stepLift, dropLift, cancelLift]
  );

  const isLifted = useCallback((pos: TPos) => activeLift !== null && Object.is(activeLift, pos), [activeLift]);

  return { liftedPos: activeLift, announcement, isLifted, registerHandle, handleKeyDown, handleBlur };
}
